/*
 * Pure display helpers for the task list rows, cards and states. `now` is a
 * parameter so the page's per-second tick stays the only clock.
 */
import moment from 'moment-timezone';
import { cityFromZone } from './local_time';
import type { CallEligibility } from './local_time';
import { filterTzAbbr, resolveFilterTz } from './filter_tz';
import { default_date_range } from './filters';
import { rule_day_label } from './rule_line';
import type { Tone } from './ui';

export interface ListRowItem {
  is_lead?: boolean | null;
  level?: string | null;
  status?: string | null;
  task_label?: string | null;
  task_type?: string | null;
  rule_day?: number | null;
  created_at_iso?: string | null;
}

// "Lead · L3" for a lead, the level alone otherwise; '' when there is nothing to say.
export function memberMeta(item: ListRowItem): string {
  const level = item.level || '';
  if (item.is_lead) return level ? `Lead · ${level}` : 'Lead';
  return level;
}

// "27 Oct, 5:40 PM" in tz; the year only when it is not the current one there.
export function shortStamp(
  iso: string | null | undefined,
  tz: string | null | undefined,
  nowMs: number
): string {
  if (!iso) return '';
  const m = moment(iso, moment.ISO_8601, true);
  if (!m.isValid()) return '';
  const zone = resolveFilterTz(tz);
  const local = m.clone().tz(zone);
  const same_year = local.year() === moment(nowMs).tz(zone).year();
  return local.format(same_year ? 'D MMM, h:mm A' : 'D MMM YYYY, h:mm A');
}

export interface TaskCell {
  label: string;
  // Muted tail: "Day 2", plus the created day while the created line is hidden.
  rule: string;
}

export function taskCell(
  item: ListRowItem,
  opts: { show_created: boolean; tz: string | null | undefined; now: number }
): TaskCell {
  const label = item.task_label || item.task_type || '—';
  const parts: string[] = [];
  const day = rule_day_label(item.rule_day, item.task_type);
  if (day) parts.push(day);
  if (!opts.show_created) {
    const created = shortStamp(item.created_at_iso, opts.tz, opts.now);
    if (created) parts.push(created.split(',')[0]);
  }
  return { label, rule: parts.join(' · ') };
}

export interface StatusMeta {
  label: string;
  tone: Tone;
}

// One tone per status everywhere; an unknown status reads as Pending.
const STATUS_META: Record<string, StatusMeta> = {
  pending: { label: 'Pending', tone: 'neutral' },
  draft: { label: 'Draft', tone: 'warning' },
  submitted: { label: 'Submitted', tone: 'success' },
  skipped: { label: 'Skipped', tone: 'outline' },
  auto_completed: { label: 'Auto completed', tone: 'outline' },
  auto_closed: { label: 'Auto closed', tone: 'info' }
};

export const statusMeta = (status: string | null | undefined): StatusMeta =>
  (status && STATUS_META[status]) || STATUS_META.pending;

export const callLabel = (call: CallEligibility): string =>
  call === 'eligible' ? 'Can call now' : 'Outside calling hours';

// What the Due cell's number means; a live instant row shows its wait instead.
export type DueKind = 'waiting' | 'overdue' | 'due';

export const dueKind = (instantLive: boolean, overdue: boolean): DueKind => {
  if (instantLive) return 'waiting';
  return overdue ? 'overdue' : 'due';
};

export const DUE_CAPTION: Record<DueKind, string> = {
  waiting: 'Waiting',
  overdue: 'Overdue',
  due: 'Due'
};

// "Kolkata (IST)"; the abbreviation alone when the zone has no city part.
export function zoneLabel(tz: string | null | undefined, now: number): string {
  const zone = resolveFilterTz(tz);
  const city = zone.includes('/') ? cityFromZone(zone) : '';
  const abbr = filterTzAbbr(zone, now);
  return city && city !== abbr ? `${city} (${abbr})` : abbr;
}

export const bandCountLabel = (n: number): string => `${n} on this page`;

// 1-based "from–to" of the rows on this page; null when there are none.
export function pageRange(
  page: number,
  pageSize: number,
  total: number
): { from: number; to: number } | null {
  if (total <= 0) return null;
  const from = (page - 1) * pageSize + 1;
  return { from, to: Math.min(page * pageSize, total) };
}

export type BodyState = 'loading' | 'error' | 'empty' | 'rows';

// Rows already on screen stay through a refresh, failed or not.
export function bodyState(loading: boolean, rowCount: number, loadError: string): BodyState {
  if (rowCount > 0) return 'rows';
  if (loading) return 'loading';
  return loadError ? 'error' : 'empty';
}

// "Clear filters": every filter back to any, the due window back to the rolling
// default. Level and sort are views, not filters, so they stay.
export function clearedFilters(tz: string | null | undefined) {
  const rolling = default_date_range(tz);
  return {
    status_filter: 'All',
    type_filter: [] as string[],
    scope_filter: 'all',
    owner_filter: [] as string[],
    call_filter: '',
    search: '',
    date_from: rolling.from,
    date_to: rolling.to,
    date_range_touched: false,
    created_from: '',
    created_to: '',
    date_field: 'due',
    page: 1
  };
}
